import React, { Component } from "react";
import { withRouter } from "react-router-dom";
import PropTypes from "prop-types";
import withStyles from "@material-ui/core/styles/withStyles";
import CircularProgress from "@material-ui/core/CircularProgress";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import DialogFooter from "components/Dialog/DialogFooter.jsx";
import MenuItem from "@material-ui/core/MenuItem";
import Select from "@material-ui/core/Select";
import InputLabel from "@material-ui/core/InputLabel";
import FormControl from "@material-ui/core/FormControl";
import Icon from "@material-ui/core/Icon";

import GridContainer from "components/Grid/GridContainer.jsx";
import GridItem from "components/Grid/GridItem.jsx";
import CardHeader from "components/Card/CardHeader.jsx";
import Button from "components/CustomButtons/Button.jsx";
import CustomInput from "components/CustomInput/CustomInput.jsx";
import DataTable, { Pill } from "components/DataTable/DataTable.jsx";
import DialogHeader from "components/Dialog/DialogHeader.jsx";

import api from "api/index";
import ViewToolbar from "components/Toolbar/ViewToolbar.jsx";

const STAT_CONFIGS = [
  { key: "total",       label: "Total",       icon: "assignment",      bg: "#e3f2fd", text: "#0d47a1", bar: "#1565c0" },
  { key: "pending",     label: "Pending",     icon: "hourglass_empty", bg: "#fff8e1", text: "#e65100", bar: "#ef6c00" },
  { key: "shortlisted", label: "Shortlisted", icon: "how_to_reg",      bg: "#e8eaf6", text: "#283593", bar: "#3949ab" },
  { key: "admitted",    label: "Admitted",    icon: "check_circle",    bg: "#e8f5e9", text: "#2e7d32", bar: "#388e3c" },
  { key: "rejected",    label: "Rejected",    icon: "cancel",          bg: "#ffebee", text: "#b71c1c", bar: "#c62828" }
];

const styles = {
  heroWrap: {
    position: "relative",
    width: "100%",
    height: "240px",
    borderRadius: "20px",
    overflow: "hidden",
    marginBottom: "30px",
    background: "linear-gradient(160deg, #0d1b6e 0%, #1a237e 60%, #1565c0 100%)",
    boxShadow: "0 20px 40px -15px rgba(26, 35, 126, 0.3)"
  },
  heroOverlay: {
    position: "absolute",
    inset: 0,
    background: "linear-gradient(to bottom, rgba(0,0,0,0) 0%, rgba(0,0,0,0.4) 100%)",
    zIndex: 1
  },
  heroContent: {
    position: "absolute",
    inset: 0,
    zIndex: 2,
    padding: "40px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-end",
    color: "#fff"
  },
  heroTitle: { fontSize: "32px", fontWeight: 800, margin: 0, letterSpacing: "-0.5px" },
  heroSub: { fontSize: "15px", margin: "8px 0 0", opacity: 0.9, fontWeight: 400 },

  // KPI stat cards
  kpiRow: { display: "flex", gap: "16px", marginBottom: "32px", flexWrap: "wrap" },
  statCard: {
    flex: 1,
    minWidth: "160px",
    padding: "20px",
    borderRadius: "16px",
    background: "#fff",
    boxShadow: "0 4px 20px 0 rgba(0,0,0,0.05)",
    display: "flex",
    alignItems: "center",
    gap: "16px",
    transition: "transform 0.2s ease",
    border: "1px solid #f1f5f9",
    "&:hover": { transform: "translateY(-4px)", boxShadow: "0 12px 30px 0 rgba(0,0,0,0.08)" }
  },
  iconBox: {
    width: "48px",
    height: "48px",
    borderRadius: "12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0
  },
  statNumber: { fontSize: "20px", fontWeight: 800, color: "#1e293b", margin: 0 },
  statLabel: { fontSize: "12px", color: "#64748b", margin: "2px 0 0", fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.4px" },

  // Toolbar & Search
  toolbarWrap: {
    background: "#fff",
    padding: "20px",
    borderRadius: "16px",
    marginBottom: "24px",
    boxShadow: "0 2px 12px 0 rgba(0,0,0,0.03)",
    border: "1px solid #f1f5f9"
  },
  searchBar: {
    display: "flex",
    gap: "12px",
    marginTop: "16px",
    alignItems: "center",
    flexWrap: "wrap"
  },
  searchInput: {
    flex: 1,
    maxWidth: "320px",
    padding: "10px 16px",
    border: "1px solid #e2e8f0",
    borderRadius: "10px",
    fontSize: "14px",
    outline: "none",
    transition: "border-color 0.2s",
    "&:focus": { borderColor: "#1a237e" }
  },
  filterSelect: {
    padding: "10px 16px",
    border: "1px solid #e2e8f0",
    borderRadius: "10px",
    fontSize: "14px",
    minWidth: "160px",
    background: "#fff",
    outline: "none",
    cursor: "pointer",
    "&:focus": { borderColor: "#1a237e" }
  },

  errorMsg: { color: "#f44336", fontSize: "13px", marginBottom: "16px" },
  pagination: { display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "20px", padding: "0 10px" },
  formControl: { width: "100%", marginTop: "16px" }
};

const STATUS_TONES = {
  pending:     "warning",
  shortlisted: "info",
  admitted:    "success",
  rejected:    "danger"
};

const BLANK = {
  applicantName: "", applicantEmail: "", phone: "",
  programmeId: "", academicYearId: "", notes: ""
};

class ApplicationList extends Component {
  state = {
    tab: 0,
    applications: [], total: 0, page: 1, totalPages: 1,
    search: "", statusFilter: "",
    programmes: [], academicYears: [],
    counts: { total: 0, pending: 0, shortlisted: 0, admitted: 0, rejected: 0 },
    loading: true, error: null,
    open: false, form: { ...BLANK }, saving: false, formError: null,
    statusDialogOpen: false, statusTarget: null, newStatus: ""
  };

  componentDidMount() {
    this.fetchAll();
  }

  fetchAll = async () => {
    this.setState({ loading: true });
    const { page, search, statusFilter } = this.state;
    try {
      const statuses = ["pending", "shortlisted", "admitted", "rejected"];
      const [aRes, pRes, yRes, allRes, ...statusRes] = await Promise.all([
        api.get("/admissions", { params: { page, limit: 20, search: search || undefined, status: statusFilter || undefined } }),
        api.get("/programmes", { params: { limit: 100 } }).catch(() => ({ data: { data: [] } })),
        api.get("/academic-years", { params: { limit: 50 } }).catch(() => ({ data: { data: [] } })),
        api.get("/admissions", { params: { limit: 1 } }).catch(() => ({ data: { total: 0 } })),
        ...statuses.map(s => api.get("/admissions", { params: { status: s, limit: 1 } }).catch(() => ({ data: { total: 0 } })))
      ]);
      const ad = aRes.data.data || aRes.data;
      const pd = pRes.data.data || pRes.data;
      const yd = yRes.data.data || yRes.data;
      const counts = { total: allRes.data.data?.total || allRes.data.total || 0 };
      statuses.forEach((s, i) => {
        const d = statusRes[i].data.data || statusRes[i].data;
        counts[s] = d.total || 0;
      });
      this.setState({
        applications: Array.isArray(ad) ? ad : ad.data || [],
        total: ad.total || (Array.isArray(ad) ? ad.length : (ad.data || []).length),
        totalPages: ad.totalPages || 1,
        programmes: Array.isArray(pd) ? pd : pd.data || [],
        academicYears: Array.isArray(yd) ? yd : yd.data || [],
        counts,
        loading: false
      });
    } catch (e) {
      this.setState({ error: "Could not load applications.", loading: false });
    }
  };

  handleChange = field => e => {
    const v = e.target.value;
    this.setState(prev => ({ form: { ...prev.form, [field]: v } }));
  };

  handleSave = async () => {
    const { form } = this.state;
    if (!form.applicantName || !form.applicantEmail || !form.programmeId || !form.academicYearId) {
      this.setState({ formError: "Name, email, programme, and academic year are required." });
      return;
    }
    this.setState({ saving: true, formError: null });
    try {
      await api.post("/admissions", form);
      this.setState({ open: false, saving: false, form: { ...BLANK } });
      this.fetchAll();
    } catch (e) {
      this.setState({ formError: e.response?.data?.message || "Failed to save application.", saving: false });
    }
  };

  openStatusDialog = (app) => {
    this.setState({ statusDialogOpen: true, statusTarget: app, newStatus: app.status || "pending" });
  };

  handleStatusUpdate = async () => {
    const { statusTarget, newStatus } = this.state;
    if (!statusTarget) return;
    this.setState({ saving: true });
    try {
      await api.patch(`/admissions/${statusTarget.id}/status`, { status: newStatus });
      this.setState({ statusDialogOpen: false, statusTarget: null, saving: false });
      this.fetchAll();
    } catch (e) {
      this.setState({ saving: false });
    }
  };

  goToPage = p => this.setState({ page: p }, this.fetchAll);

  handlePaginationChange = updater => {
    const current = { pageIndex: this.state.page - 1, pageSize: 20 };
    const next = typeof updater === "function" ? updater(current) : updater;
    this.goToPage(next.pageIndex + 1);
  };

  render() {
    const { classes } = this.props;
    const {
      applications, total, page, loading, error,
      search, statusFilter, programmes, academicYears, counts,
      open, form, saving, formError,
      statusDialogOpen, statusTarget, newStatus
    } = this.state;

    const columns = [
      { id: "applicantName", header: "Applicant Name", accessorFn: a => a.applicantName || "—" },
      { id: "applicantEmail", header: "Email", accessorFn: a => a.applicantEmail || "—" },
      { id: "phone", header: "Phone", accessorFn: a => a.phone || "—" },
      { id: "programme", header: "Programme", accessorFn: a => a.programme?.name || "—" },
      { id: "academicYear", header: "Academic Year", accessorFn: a => a.academicYear?.name || "—" },
      {
        id: "status",
        header: "Status",
        cell: info => (
          <Pill tone={STATUS_TONES[info.row.original.status] || "muted"}>
            {info.row.original.status || "pending"}
          </Pill>
        )
      },
      {
        id: "actions",
        header: "Actions",
        cell: info => (
          <div style={{ display: "flex", gap: "4px" }}>
            <Button size="sm" color="info" simple onClick={() => this.openStatusDialog(info.row.original)}>
              <Icon style={{ fontSize: "16px" }}>edit</Icon> Status
            </Button>
          </div>
        )
      }
    ];

    const emptyState = (
      <div style={{ textAlign: "center", padding: "60px", background: "#f8fafc", borderRadius: "16px", border: "1px dashed #e2e8f0" }}>
        <Icon style={{ fontSize: "48px", color: "#cbd5e1", marginBottom: "12px" }}>assignment_late</Icon>
        <p style={{ color: "#64748b", margin: 0, fontWeight: 500 }}>No applications found matching your criteria.</p>
      </div>
    );

    return (
      <GridContainer>
        <GridItem xs={12}>
          <div className={classes.heroWrap}>
            <div className={classes.heroOverlay} />
            <div className={classes.heroContent}>
              <h1 className={classes.heroTitle}>Student Admissions</h1>
              <p className={classes.heroSub}>
                Review, shortlist and process new student applications for Bishop Willis PTC.
              </p>
              <div style={{ marginTop: "20px" }}>
                <Button color="white" onClick={() => this.setState({ open: true, form: { ...BLANK }, formError: null })}>
                  <Icon style={{ marginRight: "8px" }}>add</Icon> New Application
                </Button>
              </div>
            </div>
          </div>
        </GridItem>

        <GridItem xs={12}>
          {/* KPI stat row */}
          <div className={classes.kpiRow}>
            {STAT_CONFIGS.map(cfg => (
              <div key={cfg.key} className={classes.statCard}>
                <div className={classes.iconBox} style={{ background: cfg.bg, color: cfg.bar }}>
                  <Icon>{cfg.icon}</Icon>
                </div>
                <div>
                  <p className={classes.statNumber}>{counts[cfg.key] || 0}</p>
                  <p className={classes.statLabel}>{cfg.label}</p>
                </div>
              </div>
            ))}
          </div>
        </GridItem>

        <GridItem xs={12}>
          <div className={classes.toolbarWrap}>
            <ViewToolbar
              tabs={["Pending", "Shortlisted", "Admitted", "Rejected", "All"]}
              activeTab={this.state.tab || 0}
              onTabChange={tab => {
                const map = ["pending", "shortlisted", "admitted", "rejected", ""];
                this.setState({ tab, statusFilter: map[tab] }, this.fetchAll);
              }}
            />
            <div className={classes.searchBar}>
              <input
                type="text"
                placeholder="Search name or email..."
                className={classes.searchInput}
                value={search}
                onChange={e => this.setState({ search: e.target.value })}
                onKeyDown={e => e.key === "Enter" && this.fetchAll()}
              />
              <select
                className={classes.filterSelect}
                value={statusFilter}
                onChange={e => this.setState({ statusFilter: e.target.value }, this.fetchAll)}
              >
                <option value="">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="shortlisted">Shortlisted</option>
                <option value="admitted">Admitted</option>
                <option value="rejected">Rejected</option>
              </select>
              <Button color="primary" onClick={this.fetchAll} style={{ margin: 0, borderRadius: "10px" }}>
                Search
              </Button>
              <div style={{ marginLeft: "auto", color: "#64748b", fontSize: "13px", fontWeight: 500 }}>
                {total} application{total !== 1 ? "s" : ""} found
              </div>
            </div>
          </div>

          {error && <p className={classes.errorMsg}>{error}</p>}

          <DataTable
            columns={columns}
            data={applications}
            isLoading={loading}
            emptyState={emptyState}
            pagination={{ pageIndex: page - 1, pageSize: 20 }}
            onPaginationChange={this.handlePaginationChange}
            totalCount={total}
          />
        </GridItem>

        {/* Create Application Dialog */}
        <Dialog open={open} onClose={() => this.setState({ open: false })} maxWidth="sm" fullWidth>
          <DialogHeader title="New Application" />
          <DialogContent>
            {formError && <p className={classes.errorMsg}>{formError}</p>}
            <CustomInput
              labelText="Applicant Name *"
              id="applicantName"
              formControlProps={{ fullWidth: true }}
              inputProps={{ value: form.applicantName, onChange: this.handleChange("applicantName") }}
            />
            <CustomInput
              labelText="Email Address *"
              id="applicantEmail"
              formControlProps={{ fullWidth: true }}
              inputProps={{ value: form.applicantEmail, onChange: this.handleChange("applicantEmail"), type: "email" }}
            />
            <CustomInput
              labelText="Phone Number"
              id="appPhone"
              formControlProps={{ fullWidth: true }}
              inputProps={{ value: form.phone, onChange: this.handleChange("phone"), placeholder: "+256 ..." }}
            />
            <FormControl className={classes.formControl}>
              <InputLabel>Programme *</InputLabel>
              <Select value={form.programmeId} onChange={this.handleChange("programmeId")}>
                <MenuItem value="">— Select Programme —</MenuItem>
                {programmes.map(p => (
                  <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl className={classes.formControl}>
              <InputLabel>Academic Year *</InputLabel>
              <Select value={form.academicYearId} onChange={this.handleChange("academicYearId")}>
                <MenuItem value="">— Select Year —</MenuItem>
                {academicYears.map(y => (
                  <MenuItem key={y.id} value={y.id}>{y.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <CustomInput
              labelText="Notes"
              id="appNotes"
              formControlProps={{ fullWidth: true }}
              inputProps={{
                value: form.notes,
                onChange: this.handleChange("notes"),
                multiline: true,
                rows: 3,
                placeholder: "Optional notes..."
              }}
            />
          </DialogContent>
          <DialogFooter>
            <Button outlined onClick={() => this.setState({ open: false })} disabled={saving}>Cancel</Button>
            <Button color="primary" onClick={this.handleSave} disabled={saving}>
              {saving ? <CircularProgress size={18} style={{ color: "#fff" }} /> : "Submit Application"}
            </Button>
          </DialogFooter>
        </Dialog>

        {/* Update Status Dialog */}
        <Dialog open={statusDialogOpen} onClose={() => this.setState({ statusDialogOpen: false })} maxWidth="xs" fullWidth>
          <DialogHeader title="Update Application Status" />
          <DialogContent>
            {statusTarget && (
              <p style={{ margin: "0 0 12px", color: "#555", fontSize: "14px" }}>
                Applicant: <strong>{statusTarget.applicantName}</strong>
              </p>
            )}
            <FormControl className={classes.formControl}>
              <InputLabel>New Status</InputLabel>
              <Select value={newStatus} onChange={e => this.setState({ newStatus: e.target.value })}>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="shortlisted">Shortlisted</MenuItem>
                <MenuItem value="admitted">Admitted</MenuItem>
                <MenuItem value="rejected">Rejected</MenuItem>
              </Select>
            </FormControl>
          </DialogContent>
          <DialogFooter>
            <Button outlined onClick={() => this.setState({ statusDialogOpen: false })} disabled={saving}>Cancel</Button>
            <Button color="primary" onClick={this.handleStatusUpdate} disabled={saving}>
              {saving ? <CircularProgress size={18} style={{ color: "#fff" }} /> : "Update Status"}
            </Button>
          </DialogFooter>
        </Dialog>
      </GridContainer>
    );
  }
}

ApplicationList.propTypes = { classes: PropTypes.object.isRequired, history: PropTypes.object.isRequired };
export default withRouter(withStyles(styles)(ApplicationList));
